import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { parse } from 'date-fns';
import Isvg from 'react-inlinesvg';
import _isEmpty from 'lodash/isEmpty';
import _range from 'lodash/range';
import { useSelector } from 'react-redux';

import Button from 'components/Button';
import { Card, CardTitle } from 'components/Card';
import { DropdownToggle, DropdownMenu, DropdownItemCheckbox } from 'components/Dropdown2';
import FeatureNotSupported from 'components/FeatureNotSupported';
import { Form, Dropdown } from 'components/HookForm';
import Message from 'components/Message';
import { Table } from 'components/Table';
import SaveBar from 'components/SaveBar';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import LINKS from 'constants/links';
import { servicesPropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { IA_KEY } from 'helpers/ia-logic';
import { BackNavLink, Title } from 'features/App';
import { selectActiveVenueId, selectIaLogic } from 'selectors/general';

import { messages } from './ScheduleIncrements.messages';
import './ScheduleIncrements.scss';

const MIN_HOUR_INCREMENT = 4;
const MAX_HOUR_INCREMENT = 23;
const HOUR_INCREMENTS = _range(MIN_HOUR_INCREMENT, MAX_HOUR_INCREMENT + 1).map(number =>
  `${number}`.length < 2 ? `0${number}` : `${number}`
);

const MIN_MINUTE_INCREMENT = 0;
const MAX_MINUTE_INCREMENT = 55;
const MINUTE_INCREMENTS = _range(MIN_MINUTE_INCREMENT, MAX_MINUTE_INCREMENT + 1, 5).map(number =>
  `${number}`.length < 2 ? `0${number}` : `${number}`
);

const { block, element } = bem('schedule-increments');

const FORM_NAME = 'schedulingIncrementsForm';

const INCREMENTS_FIELD_NAME = 'increments';
const SERVICE_IDS_FIELD_NAME = 'serviceIds';
const COLUMNS_FIELD_NAME = 'columns';

const ScheduleIncrements = ({
  services,
  isLoading,
  defaultValues,
  resetScheduleIncrementsForm,
  handleSubmit,
  scheduleIncrements,
}) => {
  const venueId = useSelector(selectActiveVenueId);
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.MANAGE_SERVICES, venueId));

  if (!featureIsSupported) {
    return <FeatureNotSupported />;
  }

  return (
    <div className={block()}>
      <Title>
        <BackNavLink useHistoryHook />
        <Message message={messages.title} />
      </Title>

      <Card className="mb++">
        <CardTitle>
          <Message message={messages.cardTitle} />
        </CardTitle>

        <Message as="p" message={messages.descriptionFirstParagraph} />

        <Message
          as="p"
          message={messages.descriptionSecondParagraph}
          values={{
            strong: text => <strong>{text}</strong>,
            a: text => (
              <a href={LINKS.changeAppointmentsStartTime.href} target="_blank" rel="noopener noreferrer">
                {text}
              </a>
            ),
          }}
        />

        <Form id={FORM_NAME} onSubmit={handleSubmit} defaultValues={defaultValues} isLoading={isLoading}>
          {({ watch, register, setValue, reset }) => (
            <ScheduleIncrementsForm
              services={services}
              isLoading={isLoading}
              defaultValues={defaultValues}
              resetScheduleIncrementsForm={resetScheduleIncrementsForm}
              handleSubmit={handleSubmit}
              scheduleIncrements={scheduleIncrements}
              watch={watch}
              register={register}
              setValue={setValue}
              reset={reset}
            />
          )}
        </Form>
      </Card>
    </div>
  );
};

ScheduleIncrements.propTypes = {
  services: servicesPropTypes,
  isLoading: PropTypes.bool,
  defaultValues: PropTypes.shape({
    serviceIds: PropTypes.arrayOf(PropTypes.number),
    increments: PropTypes.arrayOf(PropTypes.string),
  }),
  resetScheduleIncrementsForm: PropTypes.func,
  handleSubmit: PropTypes.func,
  scheduleIncrements: PropTypes.arrayOf(
    PropTypes.shape({
      msiServiceId: PropTypes.number,
      scheduleIncrements: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};

export const ScheduleIncrementsForm = ({
  services,
  resetScheduleIncrementsForm,
  scheduleIncrements,
  watch,
  register,
  setValue,
}) => {
  const selectedIncrements = watch(INCREMENTS_FIELD_NAME);
  const selectedServices = watch(SERVICE_IDS_FIELD_NAME);

  const setDefaultValues = () => {
    setValue(SERVICE_IDS_FIELD_NAME, []);
    setValue(INCREMENTS_FIELD_NAME, []);
    setValue(COLUMNS_FIELD_NAME, []);
  };

  const isSelectedServicesEmpty = _isEmpty(selectedServices);
  const isSelectedIncrementsEmpty = _isEmpty(selectedIncrements);
  const handleReset = () => resetScheduleIncrementsForm(() => setDefaultValues());

  useEffect(() => {
    if (!_isEmpty(selectedServices)) {
      selectedServices.forEach(id => {
        const stateServiceIncrements = scheduleIncrements.find(
          ({ msiServiceId }) => id === msiServiceId
        )?.scheduleIncrements;

        if (stateServiceIncrements) {
          const updatedValue = [...new Set([...selectedIncrements, ...stateServiceIncrements])];
          setValue(INCREMENTS_FIELD_NAME, updatedValue);
        }
      });
    }
  }, [selectedServices]);

  const handleColumnChange = (isChecked, minute) => {
    let updatedValue = [];
    const columnIncrements = HOUR_INCREMENTS.map(hour => `${hour}:${minute}`);

    if (isChecked) {
      updatedValue = [...new Set([...selectedIncrements, ...columnIncrements])];
    } else {
      updatedValue = selectedIncrements.filter(increment => !columnIncrements.find(inc => inc === increment));
    }

    setValue(INCREMENTS_FIELD_NAME, updatedValue);
  };

  const handleServiceChange = (checked, currentSelectedIncrements, id, onChange) => {
    if (!checked) {
      const newValue = currentSelectedIncrements.filter(opt => opt !== id);
      if (_isEmpty(newValue)) {
        setDefaultValues();
      }
      onChange(newValue);
    } else {
      onChange(currentSelectedIncrements.concat(id));
    }
  };

  const handleAllServiceChange = allSelected => {
    if (allSelected) {
      setDefaultValues();
    } else {
      setValue(
        SERVICE_IDS_FIELD_NAME,
        services.map(({ id }) => id)
      );
    }
  };

  return (
    <>
      <div className={element('service-selector')}>
        <Dropdown name="serviceIds" closeOnSelect={false} multiselect>
          {({ value, onChange }) => {
            const allSelected = value.length === services.length;

            return (
              <>
                <DropdownToggle className={element('dropdown-toggle')} label={messages.selectServiceLabel}>
                  <Message
                    {...(allSelected ? messages.allServicesSelected : messages.xServicesSelected)}
                    values={{ count: value.length }}
                  />
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItemCheckbox
                    isSelectAll
                    isChecked={allSelected}
                    onClick={() => handleAllServiceChange(allSelected)}
                  />
                  {services.map(({ id, name }) => (
                    <DropdownItemCheckbox
                      key={id}
                      isChecked={value.includes(id)}
                      onClick={checked => handleServiceChange(checked, value, id, onChange)}>
                      {name}
                    </DropdownItemCheckbox>
                  ))}
                </DropdownMenu>
              </>
            );
          }}
        </Dropdown>

        <When predicate={!isSelectedServicesEmpty}>
          <Message
            styled={false}
            className="ml- text--danger text--semibold"
            as={Button}
            onClick={handleReset}
            message={messages.clearSelection}
          />
        </When>
      </div>

      <Table>
        <tr className={element('increment-row')}>
          {MINUTE_INCREMENTS.map(minute => (
            <td key={minute} className={element('cell', { disabled: isSelectedServicesEmpty })}>
              <input
                {...register(COLUMNS_FIELD_NAME)}
                type="checkbox"
                onChange={event => handleColumnChange(event.target.checked, minute)}
                className={element('checkbox')}
                disabled={isSelectedServicesEmpty}
              />
            </td>
          ))}
        </tr>

        {HOUR_INCREMENTS.map(hour => (
          <tr key={hour} className={element('increment-row')}>
            {MINUTE_INCREMENTS.map(minute => {
              const currentIncrement = `${hour}:${minute}`;

              return (
                <td key={currentIncrement} className={element('cell', { disabled: isSelectedServicesEmpty })}>
                  <input
                    {...register(INCREMENTS_FIELD_NAME)}
                    type="checkbox"
                    value={currentIncrement}
                    className={element('checkbox', { disabled: isSelectedServicesEmpty })}
                    disabled={isSelectedServicesEmpty}
                  />
                  <Message format="time" value={parse(currentIncrement, 'HH:mm', new Date())} />
                </td>
              );
            })}
          </tr>
        ))}
      </Table>
      <SaveBar
        visible
        notice={
          isSelectedServicesEmpty || isSelectedIncrementsEmpty ? null : (
            <span className="mr+">
              <Isvg src={ICONS.infoYellow} className="mr-" />
              <Message message={messages.changesDelayCopy} />
            </span>
          )
        }
        submitting={false}
        formName={FORM_NAME}
        disabled={isSelectedServicesEmpty || isSelectedIncrementsEmpty}
      />
    </>
  );
};

ScheduleIncrementsForm.propTypes = {
  services: servicesPropTypes,
  resetScheduleIncrementsForm: PropTypes.func,
  scheduleIncrements: PropTypes.arrayOf(
    PropTypes.shape({
      msiServiceId: PropTypes.number,
      scheduleIncrements: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  watch: PropTypes.func,
  register: PropTypes.func,
  setValue: PropTypes.func,
};

export default ScheduleIncrements;
